'use client';

import { Form } from '@gartner-digital-markets/nimbus-v3-ui';
import type { BaseSyntheticEvent } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import {
  DEFAULT_QUALIFICATION_PAYLOAD_OPTIONS,
  getLocalizedDefaultQualificationCopy,
} from '../localization';
import { SelectField } from './SelectField';
import { SubmitButton } from './SubmitButton';

export const DEFAULT_QUALIFICATION_FIELD_NAME = 'whereAreYou';
export const TIMELINE_QUALIFICATION_FIELD_NAME = 'timeline';
export const PRICING_QUALIFICATION_FIELD_NAME = 'pricing';
export const COMPANY_SIZE_QUALIFICATION_FIELD_NAME = 'companySize';
export const FREE_TRIAL_QUALIFICATION_FIELD_NAME = 'freeTrial';

export const DEFAULT_QUALIFICATION_OPTIONS = [
  ...DEFAULT_QUALIFICATION_PAYLOAD_OPTIONS,
];
export const TIMELINE_QUALIFICATION_OPTIONS = [
  'Within the next 3 months',
  'Within the next 6 months',
  'Within the next year',
  "I'm not planning to adopt new software",
];
export const PRICING_QUALIFICATION_OPTIONS = [
  'Monthly Subscription',
  'Annual Subscription',
  'One-Time License',
  'Free Trial',
];
export const COMPANY_SIZE_QUALIFICATION_OPTIONS = [
  'Small business (1-50 employees)|Small Business',
  'Medium business (51-200 employees)|Medium Business',
  'Large business (+201 employees)|Large Business',
];
export const FREE_TRIAL_QUALIFICATION_OPTIONS = [
  'Yes|free_trial=Yes&',
  'No|no',
];

export const DEFAULT_STEP2_SUBTITLE = 'Thank you! Just one more question:';
export const DEFAULT_STEP2_QUESTION_LABEL =
  'Which best describes where you are in your software search?';
export const TIMELINE_STEP2_QUESTION_LABEL =
  'When do you plan to adopt new software?';
export const PRICING_STEP2_QUESTION_LABEL =
  'Which pricing model do you prefer?';
export const COMPANY_SIZE_STEP2_QUESTION_LABEL = 'What is your company size?';
export const FREE_TRIAL_STEP2_QUESTION_LABEL =
  'Are you interested in products with Free Trial?';
export const DEFAULT_SECOND_BUTTON_TEXT = 'Submit';

export type QualificationPresetKey =
  | 'default'
  | 'timeline'
  | 'pricing'
  | 'companySize'
  | 'freeTrial';

export interface QualificationPresetFlags {
  isTimeline?: boolean;
  isPricing?: boolean;
  isCompanySize?: boolean;
  isFreeTrial?: boolean;
}

interface QualificationPresetDefinition {
  fieldName: string;
  label: string;
  options: string[];
}

const QUALIFICATION_PRESET_MAP: Record<
  QualificationPresetKey,
  QualificationPresetDefinition
> = {
  default: {
    fieldName: DEFAULT_QUALIFICATION_FIELD_NAME,
    label: DEFAULT_STEP2_QUESTION_LABEL,
    options: DEFAULT_QUALIFICATION_OPTIONS,
  },
  timeline: {
    fieldName: TIMELINE_QUALIFICATION_FIELD_NAME,
    label: TIMELINE_STEP2_QUESTION_LABEL,
    options: TIMELINE_QUALIFICATION_OPTIONS,
  },
  pricing: {
    fieldName: PRICING_QUALIFICATION_FIELD_NAME,
    label: PRICING_STEP2_QUESTION_LABEL,
    options: PRICING_QUALIFICATION_OPTIONS,
  },
  companySize: {
    fieldName: COMPANY_SIZE_QUALIFICATION_FIELD_NAME,
    label: COMPANY_SIZE_STEP2_QUESTION_LABEL,
    options: COMPANY_SIZE_QUALIFICATION_OPTIONS,
  },
  freeTrial: {
    fieldName: FREE_TRIAL_QUALIFICATION_FIELD_NAME,
    label: FREE_TRIAL_STEP2_QUESTION_LABEL,
    options: FREE_TRIAL_QUALIFICATION_OPTIONS,
  },
};

export interface QualificationConfigResolutionParams
  extends QualificationPresetFlags {
  label?: string;
  lang?: string | null;
  qualificationOptions?: readonly string[] | null;
}

export interface ResolvedQualificationConfig {
  presetKey: QualificationPresetKey;
  fieldName: string;
  label: string;
  options: readonly string[];
}

const hasNonEmptyQualificationOptions = (
  options?: readonly string[] | null
): options is readonly string[] => Array.isArray(options) && options.length > 0;

export const resolveQualificationPresetKey = ({
  isTimeline,
  isPricing,
  isCompanySize,
  isFreeTrial,
}: QualificationPresetFlags): QualificationPresetKey => {
  if (isTimeline) {
    return 'timeline';
  }
  if (isPricing) {
    return 'pricing';
  }
  if (isCompanySize) {
    return 'companySize';
  }
  if (isFreeTrial) {
    return 'freeTrial';
  }
  return 'default';
};

export const resolveQualificationConfig = ({
  label,
  lang,
  qualificationOptions,
  ...presetFlags
}: QualificationConfigResolutionParams): ResolvedQualificationConfig => {
  const presetKey = resolveQualificationPresetKey(presetFlags);
  const presetDefinition = QUALIFICATION_PRESET_MAP[presetKey];
  const localizedDefaultQualificationCopy =
    getLocalizedDefaultQualificationCopy(lang);

  return {
    presetKey,
    fieldName: presetDefinition.fieldName,
    label:
      label ??
      (presetKey === 'default'
        ? localizedDefaultQualificationCopy.label
        : presetDefinition.label),
    options: hasNonEmptyQualificationOptions(qualificationOptions)
      ? qualificationOptions
      : presetKey === 'default'
        ? localizedDefaultQualificationCopy.options
        : presetDefinition.options,
  };
};

export const createQualificationStepSchema = (fieldName: string) =>
  z.object({
    [fieldName]: z.string().min(1, 'Field is required'),
  } as Record<string, z.ZodString>);

export const createQualificationStepDefaultValues = (fieldName: string) =>
  ({
    [fieldName]: '',
  }) as Record<string, string>;

export const qualificationStepSchema = createQualificationStepSchema(
  DEFAULT_QUALIFICATION_FIELD_NAME
);

export type QualificationStepValues = z.infer<typeof qualificationStepSchema>;

interface DefaultQualificationStepProps<
  TFieldValues extends FieldValues = QualificationStepValues,
> {
  form: UseFormReturn<TFieldValues>;
  handleSubmit: (event?: BaseSyntheticEvent) => Promise<void> | void;
  isLoading: boolean;
  name?: string;
  buttonText?: string;
  label?: string;
  options?: readonly string[];
  buttonPlacement?: 'stacked' | 'inline';
  lang?: string | null;
}

export const DefaultQualificationStep = <
  TFieldValues extends FieldValues = QualificationStepValues,
>({
  form,
  handleSubmit,
  isLoading,
  name = DEFAULT_QUALIFICATION_FIELD_NAME,
  buttonText = DEFAULT_SECOND_BUTTON_TEXT,
  label,
  options,
  buttonPlacement = 'stacked',
  lang,
}: DefaultQualificationStepProps<TFieldValues>) => {
  const localizedDefaultQualificationCopy =
    getLocalizedDefaultQualificationCopy(lang);
  const resolvedLabel = label ?? localizedDefaultQualificationCopy.label;
  const resolvedOptions = options ?? localizedDefaultQualificationCopy.options;

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6 pb-6">
        {buttonPlacement === 'stacked' ? (
          <>
            <div className="w-full">
              <SelectField
                name={name}
                label={resolvedLabel}
                options={resolvedOptions}
              />
            </div>
            <div className="flex justify-end">
              <SubmitButton text={buttonText} isLoading={isLoading} />
            </div>
          </>
        ) : (
          <>
            {/* Desktop uses `items-start` so the select column can grow
                downward (when Nimbus `<FormMessage>` appears below the
                trigger) without shifting the submit button. `md:mt-8`
                offsets the button wrapper past the label height so the
                button visually aligns with the select trigger on the
                same row. The validation message is rendered by
                `<FormMessage>` inside `<FormItem>` (i.e. inside
                `SelectField`) so the select trigger's `aria-describedby`
                resolves to a real id and screen readers announce the
                error. Mobile is a vertical stack: label, select, error,
                button. */}
            <div className="flex w-full flex-col gap-4 md:flex-row md:items-start">
              <div className="w-full flex-1">
                <SelectField
                  name={name}
                  label={resolvedLabel}
                  options={resolvedOptions}
                />
              </div>
              <div className="w-full md:mt-8 md:w-auto">
                <SubmitButton
                  text={buttonText}
                  isLoading={isLoading}
                  className="h-9 w-full md:w-auto"
                />
              </div>
            </div>
          </>
        )}
      </form>
    </Form>
  );
};
